import { ApartmentUpdate } from "@/store/slices/apartments";
import { isValidHttpUrl } from "@/pages/is-valid-http-url";
import type { NewStrOwner } from "@/lib/shadcn/components/str-owner-select";
import type { GetStrOwnersResponse } from "@/models/responses";

/**
 * validateStrOwnerSelection is the submit-blocking guard for the STR card on the
 * add-apartment form. It encodes, in order, the three edge cases the operator can
 * hit before the POST fires; the first that trips wins and returns its message,
 * else `null` (nothing to block). Pure — no state, no side effects — so the page
 * can call it once in `handleSaveChanges` and drive the inline error off the
 * return.
 *
 * per-apartment-str-owner/EC-001: subject to STR but invoicing is N/A (MyData
 * required). /EC-002: subject to STR with neither an existing owner picked nor a
 * new one entered (gated on the owners list having loaded — until then the
 * operator couldn't have chosen). Since the single-column model has no wire flag
 * for "subject" (D-R2), `subject` is the checkbox's pure UI state; the server
 * treats "no owner" as STR-exempt, so EC-002 is a purely client-side block.
 * /EC-003: "＋ New owner" chosen but a required field is empty.
 */
export function validateStrOwnerSelection(
    subject: boolean,
    form: Pick<ApartmentUpdate, "neteraCredentialsId" | "strOwnerId">,
    newStrOwner: NewStrOwner | null,
    strOwnersData: GetStrOwnersResponse | null | undefined,
): string | null {
    if (subject && form.neteraCredentialsId == null) {
        return "Subject to STR upload requires a MyData invoicing account (choose an account above).";
    }
    if (strOwnersData != null && subject && form.strOwnerId == null && newStrOwner == null) {
        return "Select an existing STR owner or add a new one.";
    }
    if (
        newStrOwner != null &&
        (!newStrOwner.name.trim() || !newStrOwner.taxisUsername.trim() || !newStrOwner.taxisPassword.trim())
    ) {
        return "New owner requires a name, Taxis username, and Taxis password.";
    }
    return null;
}

/**
 * hasAddress is the client-side half of address-normalization/FR-014: an
 * apartment cannot be created or updated without naming the building it is in.
 * The server refuses it too, but a raw 400 is not an answer to give an operator
 * who simply has not picked yet — the form declines to submit instead.
 */
export function hasAddress(form: Pick<ApartmentUpdate, "addressId">): boolean {
    return form.addressId != null && form.addressId > 0;
}

/**
 * missingRequiredFields NAMES the fields still blocking the new-apartment save,
 * in the order the form presents them. Every Keros property is channel-managed
 * via HostHub, so a HostHub rental id is mandatory at creation (it is also
 * enforced server-side; this keeps the Save button honest).
 *
 * This is the single source of truth for the submit gate: `isNewApartmentValid`
 * is derived from it rather than restating the clauses. An earlier revision kept
 * the two as separate lists — the gate here and the operator-facing explanation
 * in the page — which meant a new required field could start blocking Save while
 * the explanation stayed silent about it. Whoever is told WHY the button is
 * dimmed must be told by the same code that dims it.
 */
export function missingRequiredFields(
    form: ApartmentUpdate,
    apartmentOwners: number[],
    payoutRatio: number,
    managementFeeRatio: number,
): string[] {
    const missing: string[] = [];
    if (form.name === "") missing.push("Apartment Name");
    if (form.numberOfBeds === undefined || form.numberOfBeds <= 0) missing.push("N. Bedrooms");
    if (!hasAddress(form)) missing.push("Address");
    if (form.imageUrl === undefined || !isValidHttpUrl(form.imageUrl)) missing.push("Image Url");
    if (apartmentOwners.length === 0) missing.push("Owners");
    if (form.ownerGroupId <= 0) missing.push("Owner Group");
    if (form.groupId <= 0) missing.push("Group");
    if (form.hosthubRentalId === undefined || form.hosthubRentalId.trim() === "") {
        missing.push("HostHub Rental");
    }
    if (payoutRatio <= 0 || payoutRatio > 100) missing.push("Payout Ratio");
    if (managementFeeRatio <= 0 || managementFeeRatio >= 100) missing.push("Management Fee Ratio");
    return missing;
}

/** isNewApartmentValid is the submit gate: nothing required is still missing. */
export function isNewApartmentValid(
    form: ApartmentUpdate,
    apartmentOwners: number[],
    payoutRatio: number,
    managementFeeRatio: number,
): boolean {
    return missingRequiredFields(form, apartmentOwners, payoutRatio, managementFeeRatio).length === 0;
}
